<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164 } from 'shared/helpers/Validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

// Available voice providers. DawaTech Caller is additive beside Twilio.
const PROVIDERS = [
  { value: 'twilio', label: 'Twilio' },
  { value: 'dawatechcaller', label: 'DawaTech Caller' },
];

const state = reactive({
  provider: 'twilio',
  phoneNumber: '',
  // Twilio
  accountSid: '',
  authToken: '',
  apiKeySid: '',
  apiKeySecret: '',
  // DawaTech Caller (talks to the Node voice-bridge; Fonoster creds live there)
  bridgeUrl: '',
  sharedSecret: '',
});

const isTwilio = computed(() => state.provider === 'twilio');
const isDawatechCaller = computed(() => state.provider === 'dawatechcaller');

const uiFlags = useMapGetter('inboxes/getUIFlags');

const validationRules = {
  phoneNumber: { required, isPhoneE164 },
  accountSid: { required: requiredIf(() => state.provider === 'twilio') },
  authToken: { required: requiredIf(() => state.provider === 'twilio') },
  apiKeySid: { required: requiredIf(() => state.provider === 'twilio') },
  apiKeySecret: { required: requiredIf(() => state.provider === 'twilio') },
  bridgeUrl: {
    required: requiredIf(() => state.provider === 'dawatechcaller'),
  },
  sharedSecret: {
    required: requiredIf(() => state.provider === 'dawatechcaller'),
  },
};

const v$ = useVuelidate(validationRules, state);
const isSubmitDisabled = computed(() => v$.value.$invalid);

const formErrors = computed(() => ({
  phoneNumber: v$.value.phoneNumber?.$error
    ? t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.ERROR')
    : '',
  accountSid: v$.value.accountSid?.$error
    ? t('INBOX_MGMT.ADD.VOICE.TWILIO.ACCOUNT_SID.REQUIRED')
    : '',
  authToken: v$.value.authToken?.$error
    ? t('INBOX_MGMT.ADD.VOICE.TWILIO.AUTH_TOKEN.REQUIRED')
    : '',
  apiKeySid: v$.value.apiKeySid?.$error
    ? t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.REQUIRED')
    : '',
  apiKeySecret: v$.value.apiKeySecret?.$error
    ? t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.REQUIRED')
    : '',
  bridgeUrl: v$.value.bridgeUrl?.$error
    ? 'Voice bridge URL is required (e.g. https://dawatechcaller.dawatech.com/voice-bridge)'
    : '',
  sharedSecret: v$.value.sharedSecret?.$error ? 'Shared secret is required' : '',
}));

function getProviderConfig() {
  if (state.provider === 'dawatechcaller') {
    return {
      bridge_url: state.bridgeUrl,
      shared_secret: state.sharedSecret,
    };
  }
  return {
    account_sid: state.accountSid,
    auth_token: state.authToken,
    api_key_sid: state.apiKeySid,
    api_key_secret: state.apiKeySecret,
  };
}

async function createChannel() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    const channel = await store.dispatch('inboxes/createVoiceChannel', {
      name: `Voice (${state.phoneNumber})`,
      voice: {
        phone_number: state.phoneNumber,
        provider: state.provider,
        provider_config: getProviderConfig(),
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: channel.id },
    });
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('INBOX_MGMT.ADD.VOICE.API.ERROR_MESSAGE')
    );
  }
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.VOICE.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.VOICE.DESC')"
    />

    <form
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="createChannel"
    >
      <!-- Provider selector -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">Provider</label>
        <div class="flex gap-2">
          <button
            v-for="p in PROVIDERS"
            :key="p.value"
            type="button"
            class="px-3 py-1.5 text-sm rounded-lg border"
            :class="
              state.provider === p.value
                ? 'bg-n-blue-9 text-white border-n-blue-9'
                : 'border-n-weak text-n-slate-12 hover:bg-n-alpha-2'
            "
            @click="state.provider = p.value"
          >
            {{ p.label }}
          </button>
        </div>
      </div>

      <Input
        v-model="state.phoneNumber"
        :label="t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.PLACEHOLDER')"
        :message="formErrors.phoneNumber"
        :message-type="formErrors.phoneNumber ? 'error' : 'info'"
        @blur="v$.phoneNumber?.$touch"
      />

      <!-- Twilio fields -->
      <template v-if="isTwilio">
        <Input
          v-model="state.accountSid"
          :label="t('INBOX_MGMT.ADD.VOICE.TWILIO.ACCOUNT_SID.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VOICE.TWILIO.ACCOUNT_SID.PLACEHOLDER')"
          :message="formErrors.accountSid"
          :message-type="formErrors.accountSid ? 'error' : 'info'"
          @blur="v$.accountSid?.$touch"
        />

        <Input
          v-model="state.authToken"
          type="password"
          :label="t('INBOX_MGMT.ADD.VOICE.TWILIO.AUTH_TOKEN.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VOICE.TWILIO.AUTH_TOKEN.PLACEHOLDER')"
          :message="formErrors.authToken"
          :message-type="formErrors.authToken ? 'error' : 'info'"
          @blur="v$.authToken?.$touch"
        />

        <Input
          v-model="state.apiKeySid"
          :label="t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.PLACEHOLDER')"
          :message="formErrors.apiKeySid"
          :message-type="formErrors.apiKeySid ? 'error' : 'info'"
          @blur="v$.apiKeySid?.$touch"
        />

        <Input
          v-model="state.apiKeySecret"
          type="password"
          :label="t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.LABEL')"
          :placeholder="
            t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.PLACEHOLDER')
          "
          :message="formErrors.apiKeySecret"
          :message-type="formErrors.apiKeySecret ? 'error' : 'info'"
          @blur="v$.apiKeySecret?.$touch"
        />
      </template>

      <!-- DawaTech Caller fields -->
      <template v-else-if="isDawatechCaller">
        <Input
          v-model="state.bridgeUrl"
          label="Voice bridge URL"
          placeholder="https://dawatechcaller.dawatech.com/voice-bridge"
          :message="formErrors.bridgeUrl"
          :message-type="formErrors.bridgeUrl ? 'error' : 'info'"
          @blur="v$.bridgeUrl?.$touch"
        />

        <Input
          v-model="state.sharedSecret"
          type="password"
          label="Shared secret"
          placeholder="The secret dawaChat uses to authenticate to the voice bridge"
          :message="formErrors.sharedSecret"
          :message-type="formErrors.sharedSecret ? 'error' : 'info'"
          @blur="v$.sharedSecret?.$touch"
        />

        <p class="-mt-2 text-xs text-n-slate-11">
          DawaTech Caller connects through your dawatechcaller voice bridge. The
          Fonoster credentials and Voice Application live on that server — dawaChat
          only needs the bridge URL and shared secret. In-browser calling is
          enabled once the number/trunk and bridge are live.
        </p>
      </template>

      <div>
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.VOICE.SUBMIT_BUTTON')"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
